import { useState, useMemo } from 'react';
import { ProfileSection, useNextgenRouter, FAIcon, useConfig, useSession, UserType } from '@nextgen-web-framework/all';
import { LoginWrapper, LogoutModal } from '@commerce-identity-webcomponents/ui';
import { getLoginProps, getLogoutProps, MePanelLanguage, MePanelSharebar } from '@commerce-webcomponents/ui';
import { mySopPrevNavignationCleanup } from '@commerce-subscription-webcomponents/sdk';
import {
  ContainerElementProps,
  MePanelProps,
  ProfileComponent,
  ProfileListInterface,
  ProfileSectionPropsTypes,
  ShareUserType,
} from './types/propsTypes';
import styles from './MePanel.module.scss';

const LoginContainer = ({ children }: ContainerElementProps) => {
  const router = useNextgenRouter();
  return <LoginWrapper {...getLoginProps(router.params?.locale as string)}>{children}</LoginWrapper>;
};

const MePanel = (props: MePanelProps) => {
  const router = useNextgenRouter();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { postLogoutRedirectUri } = getLogoutProps();
  const { data, locale, langHandler, cartListId } = props || {};
  const configs = useConfig();
  const { session } = useSession();

  const createSharebarContainer = (userType: ShareUserType, displayName: string) =>
    useMemo(() => {
      const Component = ({ children }: ContainerElementProps) => (
        <MePanelSharebar userType={userType}>{children}</MePanelSharebar>
      );
      Component.displayName = displayName;
      return Component;
    }, [userType]);

  const SharebarABOContainer = createSharebarContainer(UserType.ABO, 'SharebarABOContainer');
  const SharebarMemberContainer = createSharebarContainer(UserType.MEMBER, 'SharebarMemberContainer');
  const SharebarCustomerContainer = createSharebarContainer(UserType.REGISTERED, 'SharebarCustomerContainer');

  const LanguageContainer = useMemo(() => {
    const LanaguageComponent = ({ children }: ContainerElementProps) => (
      <MePanelLanguage langHandler={langHandler} locale={locale}>
        {children}
      </MePanelLanguage>
    );
    LanaguageComponent.displayName = 'LanguageContainer';
    return LanaguageComponent;
  }, []);

  const handleProfileData = (item: ProfileListInterface): ProfileListInterface => {
    switch (item.component) {
      case ProfileComponent.signin:
        return { ...item, ContainerElement: LoginContainer };
      case ProfileComponent.signup:
        return { ...item, onClick: () => handleSignUp() };
      case ProfileComponent.link:
        return {
          ...item,
          onClick: () =>
            handleOnClick({
              url: item.reDirectUrl ?? '',
              target: item.target,
            }),
        };
      case ProfileComponent.signout:
        return { ...item, onClick: () => handleLogout() };
      case ProfileComponent.wishlist:
        return { ...item, onClick: () => handleWishlist() };
      case ProfileComponent.sso:
        return {
          ...item,
          onClick: () =>
            handleOnClick({
              url: item.reDirectUrl ?? '',
              target: item.target,
              isSSO: true,
            }),
        };
      case ProfileComponent.language:
        return {
          ...item,
          ContainerElement: LanguageContainer,
        };
      case ProfileComponent.sharebar_member:
        return {
          ...item,
          ContainerElement: SharebarMemberContainer,
        };
      case ProfileComponent.sharebar_abo:
        return {
          ...item,
          ContainerElement: SharebarABOContainer,
        };
      case ProfileComponent.sop:
        return {
          ...item,
          onClick: () =>
            handleOnClick({
              url: item.reDirectUrl ?? '',
              target: item.target,
              isSop: true,
            }),
        };
      case ProfileComponent.sharebar_customer:
        return {
          ...item,
          ContainerElement: SharebarCustomerContainer,
        };
      default:
        return item;
    }
  };
  const handleWishlist = () => {
    if (cartListId) {
      router.push(`/list/${cartListId}`);
      props.closeHandler?.();
    }
  };
  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const handleSignUp = () => {
    const redirectUrl = configs.NEXT_PUBLIC_REGISTER_REDIRECT_URL ?? '/complete-your-profile';
    router.push(`/signup?flowName=${configs.NEXT_PUBLIC_REGISTER_FLOW}&returnUri=${redirectUrl}`);
  };

  const handleOnClick = ({
    url,
    target,
    isSSO,
    isSop,
  }: {
    url: string;
    target?: string;
    isSSO?: boolean;
    isSop?: boolean;
  }) => {
    if (isSop) {
      mySopPrevNavignationCleanup();
    }
    const hrefUrl = url;
    if (hrefUrl && target) {
      const urlParams = new URL(hrefUrl);
      if (isSSO) {
        urlParams.searchParams.append('locale', PreferenceService.getPreferences().locale);
        urlParams.searchParams.append('abo', session.aboNum ?? '');
      }
      window.open(`${urlParams.toString()}`, target);
    } else {
      hrefUrl !== '' && router?.push && router.push(hrefUrl);
      props.closeHandler?.();
    }
  };
  const MePanelData = data?.map((profile: ProfileSectionPropsTypes) => {
    const { profileData, title } = profile || {};
    const subItemDetail = profileData?.map((item: ProfileListInterface) => ({
      ...item,
      ...handleProfileData(item),
      rightIcon: <FAIcon iconName="chevron-right" />,
    }));
    return {
      profileData: subItemDetail,
      title: title,
    };
  });
  return (
    <>
      <div className={styles['me-panel-container']} data-testid="me-panel-container-id">
        <div className={styles['me-panel-profile-section']}>
          {MePanelData?.map((profile, index) => (
            <div key={profile.title} data-testid={`mepanel-profile-container-id-${index}`}>
              <ProfileSection key={profile.title} title={profile.title} profileData={profile.profileData ?? []} />
              {MePanelData.length !== index + 1 && <div className={styles['me-panel-bottom-border']}></div>}
            </div>
          ))}
        </div>
      </div>
      {showLogoutModal && (
        <LogoutModal onClose={() => setShowLogoutModal(false)} postLogoutRedirectUri={postLogoutRedirectUri} />
      )}
    </>
  );
};
export default MePanel;
